import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import Drawer from '~/components/Drawer';
import {
    sectionStyles,
    labelStyles,
    requiredStyles,
    getSelectStyles,
} from './styles';
import { fetchLetterTemplates, downloadMergedDocx } from '~/services/campaign';
import toast from 'react-hot-toast';

interface Props {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    campaign: any;
    patient: any;
}

interface LetterOption {
    value: number;
    label: string;
}

interface RecipientOption {
    value: number;
    label: string;
}

const FilesDrawer: React.FC<Props> = ({
    isOpen,
    setIsOpen,
    campaign,
    patient,
}) => {
    const [letterOptions, setLetterOptions] = useState<LetterOption[]>([]);
    const [selectedLetter, setSelectedLetter] = useState<LetterOption | null>(
        null,
    );
    const [selectedRecipients, setSelectedRecipients] = useState<
        RecipientOption[]
    >([]);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isLoadingLetters, setIsLoadingLetters] = useState(false);

    const campaignId = campaign?.campaign_id || campaign?.id;

    useEffect(() => {
        if (!isOpen || !campaignId) return;

        setSelectedLetter(null);
        setSelectedRecipients([]);

        setIsLoadingLetters(true);
        fetchLetterTemplates(campaignId)
            .then((templates) => {
                setLetterOptions(
                    templates.map((t) => ({ value: t.id, label: t.name })),
                );
            })
            .catch((err) => {
                console.error('Error fetching letter templates:', err);
                toast.error('Failed to load letter templates');
            })
            .finally(() => setIsLoadingLetters(false));
    }, [isOpen, campaignId]);

    const recipientOptions: RecipientOption[] = React.useMemo(() => {
        const options: RecipientOption[] = [];
        if (patient) {
            const name = patient.person
                ? `${patient.person.first_name} ${patient.person.last_name}`
                : 'Patient';
            options.push({ value: patient.id, label: `${name} (Patient)` });

            if (patient.medical_professional) {
                const mp = patient.medical_professional;
                const mpName = mp.person
                    ? `${mp.person.first_name} ${mp.person.last_name}`
                    : 'Medical Professional';
                options.push({
                    value: mp.id,
                    label: `${mpName} (Med Pro)`,
                });
            }
        }
        if (campaign?.team_members) {
            for (const tm of campaign.team_members) {
                const tmName = tm.person
                    ? `${tm.person.first_name} ${tm.person.last_name}`
                    : 'Team Member';
                const parts: string[] = [];
                if (tm.is_primary_contact) parts.push('Primary');
                if (tm.relationship_to_patient?.name)
                    parts.push(tm.relationship_to_patient.name);
                const desc = parts.length > 0 ? ` (${parts.join(', ')})` : '';
                options.push({
                    value: tm.id,
                    label: `${tmName}${desc}`,
                });
            }
        }
        return options;
    }, [patient, campaign?.team_members]);

    const handleDownload = async () => {
        if (!selectedLetter) {
            toast.error('Please select a letter template');
            return;
        }
        if (selectedRecipients.length === 0) {
            toast.error('Please select at least one recipient');
            return;
        }

        setIsDownloading(true);
        try {
            await downloadMergedDocx(
                campaignId,
                selectedLetter.value,
                selectedRecipients.map((r) => r.value),
            );
            toast.success('Document downloaded');
        } catch (err: any) {
            console.error('Error downloading merged docx:', err);
            toast.error(err.message || 'Failed to download document');
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <Drawer isOpen={isOpen} setIsOpen={setIsOpen} title='Files'>
            <h2 className={sectionStyles}>LETTERS MERGED</h2>
            <ol className='mt-4 list-decimal space-y-1 pl-5 text-[14px]/[20px]'>
                <li>Select the letter to print.</li>
                <li>Select the letter recipient(s).</li>
                <li>Click the "Download Merged Letter" button.</li>
                <li>Print or email the merged document using MS Word.</li>
            </ol>

            <label className={labelStyles}>
                Letter <span className={requiredStyles}>*</span>
            </label>
            <Select
                menuPlacement='auto'
                options={letterOptions}
                value={selectedLetter}
                onChange={(val) => setSelectedLetter(val)}
                placeholder='Select'
                isClearable
                isLoading={isLoadingLetters}
                styles={getSelectStyles(!!selectedLetter)}
            />

            <label className={labelStyles}>
                Recipient(s) <span className={requiredStyles}>*</span>
            </label>
            <Select
                menuPlacement='auto'
                options={recipientOptions}
                value={selectedRecipients}
                onChange={(val) =>
                    setSelectedRecipients(val as RecipientOption[])
                }
                placeholder='Select'
                isMulti
                isClearable
                styles={getSelectStyles(selectedRecipients.length > 0)}
            />

            <button
                type='button'
                className='mt-[24px] rounded-full border-[2px] border-black px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:bg-black hover:text-white disabled:cursor-not-allowed disabled:opacity-50'
                onClick={handleDownload}
                disabled={
                    isDownloading ||
                    !selectedLetter ||
                    selectedRecipients.length === 0
                }
            >
                {isDownloading ? 'Downloading...' : 'Download'}
            </button>

            <div className='mt-[30px] flex gap-[16px]'>
                <button
                    type='button'
                    className='grow rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                    onClick={() => setIsOpen(false)}
                >
                    Cancel
                </button>
            </div>
        </Drawer>
    );
};

export default FilesDrawer;
